import { useStation } from '@app/core/stations/hooks/useStation';
import { StreamerBoxIcon } from '@app/modules/fleet/legacy/components/StreamerBoxIcon';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

const goToDeviceMessage = defineMessage({
  id: 'logger.streamBox.goToDevice',
  defaultMessage: 'Ir al dispositivo',
});

interface Props {
  stationId: string;
  compact?: boolean;
}

/**
 * Link to the fleet devices list filtered by this station's device id.
 * Compact mode shows an icon button; otherwise an outlined chip with the device id.
 */
export function StreamerBoxStatus(p: Props) {
  const { stationId, compact = false } = p;
  const station = useStation(stationId);

  const deviceId = station.featuresConfig?.streamerBox?.deviceId;

  // Only render if the station has a deviceId configured
  if (!deviceId) {
    return null;
  }

  // New fleet devices list filtered by this device id (not the legacy detail page).
  const deviceListTo = `/devices?id=${encodeURIComponent(deviceId)}`;

  if (compact) {
    return (
      <Tooltip title={<FormattedMessage {...goToDeviceMessage} />}>
        <IconButton
          component={Link}
          to={deviceListTo}
          size="small"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation()}
          sx={{ mr: 0.5, color: 'text.secondary' }}
        >
          <StreamerBoxIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={<FormattedMessage {...goToDeviceMessage} />}>
      <Chip
        size="small"
        variant="outlined"
        sx={{
          mr: 1.5,
          pl: 0.5,
          cursor: 'pointer',
          color: 'text.secondary',
          // Bootstrap `a:hover` would otherwise force link-blue on Chip-as-Link.
          '&:hover': { color: 'text.secondary' },
        }}
        icon={<StreamerBoxIcon fontSize="small" />}
        to={deviceListTo}
        component={Link}
        label={deviceId}
        onClick={(e) => e.stopPropagation()}
      />
    </Tooltip>
  );
}
